// Composant pour charger automatiquement les données depuis l'API
import { useEffect, useRef, useState } from 'react';
import { useModulesApi } from '../Hooks/useModulesApi';
import type { Config, LayoutItem } from '../Config/config';
import { isConfigLocalStorageEnabled } from '../Config/devMode';

interface ApiDataLoaderProps {
    config: Config;
    onConfigChange: (newConfig: Partial<Config>) => void;
    currentPageUrl: string;
    onPageLoadStateChange?: (pageUrl: string, state: 'loading' | 'loaded' | 'error') => void;
}

export const ApiDataLoader: React.FC<ApiDataLoaderProps> = ({ 
    config, 
    onConfigChange,
    currentPageUrl,
    onPageLoadStateChange
}) => {
    // ⚠️ Important: on doit charger par page, pas une seule fois globalement,
    // sinon la navigation affiche les valeurs de la config locale tant qu’on ne recharge pas.
    const loadedPagesRef = useRef<Set<string>>(new Set());
    const inFlightPagesRef = useRef<Set<string>>(new Set());
    const [tick, setTick] = useState(0); // permet de forcer un re-render après ajout dans loadedPagesRef
    const { loadInstances, isReady } = useModulesApi(config.api);

    useEffect(() => {
        const loadDataFromApi = async () => {
            // Vérifier que l'API est activée et prête
            if (!config.api.enableSync || !isReady) return;
            if (!config.api.autoLoadParams) return;
            
            // 🆕 Toujours charger les données API pour injecter les données mises à jour
            console.log('🔄 ApiDataLoader: Chargement des données API pour injection...');
            
            // Vérifier que onConfigChange est bien une fonction
            if (typeof onConfigChange !== 'function') {
                console.error('❌ ApiDataLoader: onConfigChange n\'est pas une fonction');
                return;
            }

            try {
                console.log('🔄 ApiDataLoader: Chargement des instances depuis l\'API...');
                console.log(`📍 Page actuelle: ${currentPageUrl}`);
                
                // Normaliser la page (vider vide/undefined devient '/')
                const normalizedPageUrl = currentPageUrl || '/';
                console.log(`📍 Page normalisée: ${normalizedPageUrl}`);

                // Déduplication par page
                if (loadedPagesRef.current.has(normalizedPageUrl)) {
                    return;
                }
                if (inFlightPagesRef.current.has(normalizedPageUrl)) {
                    return;
                }
                inFlightPagesRef.current.add(normalizedPageUrl);
                onPageLoadStateChange?.(normalizedPageUrl, 'loading');
                
                // Charger les instances de la page actuelle
                const allInstances = await loadInstances(normalizedPageUrl);
                
                console.log(`📦 Instances reçues:`, allInstances);
                
                if (allInstances.length > 0) {
                    console.log(`✅ ${allInstances.length} instances chargées depuis l\'API`);
                    
                    // Sauvegarder la config originale si pas déjà fait
                    if (isConfigLocalStorageEnabled(config) && !localStorage.getItem('gandc_config_original')) {
                        localStorage.setItem('gandc_config_original', JSON.stringify(config));
                        console.log('💾 Configuration originale sauvegardée');
                    }
                    
                    // 🆕 Créer une map des instances API pour faciliter le remplacement
                    const apiInstancesMap = new Map(allInstances.map(instance => [instance.id, instance]));
                    
                    console.log('🔀 Instances API disponibles:', Array.from(apiInstancesMap.keys()));
                    console.log('📊 Détail des données API:', allInstances.map(i => ({
                        id: i.id,
                        component: i.component,
                        props: i.props,
                        page: i.page
                    })));
                    
                    // Fusionner avec la config actuelle
                    const currentPage = config.PagesList.find(p => p.pageUrl === normalizedPageUrl);
                    
                    if (currentPage?.gridLayout) {
                        // Page avec grille personnalisée
                        const updatedPages = config.PagesList.map(page => {
                            if (page.pageUrl === normalizedPageUrl && page.gridLayout) {
                                // 🆕 Injecter les données API dans les items locaux existants
                                const updatedItems = page.gridLayout.items.map(item => {
                                    const apiInstance = apiInstancesMap.get(item.id);
                                    if (apiInstance) {
                                        const apiProps = apiInstance.props ?? apiInstance.data ?? {};
                                        console.log(`🔄 Injection données API pour ${item.id}:`, apiProps);
                                        // Fusionner les données API dans l'item local
                                        return {
                                            ...item,
                                            props: {
                                                ...item.props,
                                                ...apiProps
                                            }
                                        };
                                    }
                                    return item;
                                });
                                
                                return {
                                    ...page,
                                    gridLayout: {
                                        ...page.gridLayout,
                                        items: updatedItems
                                    }
                                };
                            }
                            return page;
                        });
                        
                        console.log('📄 Mise à jour des pages avec données API');
                        onConfigChange({ PagesList: updatedPages });
                    } else {
                        // Grille globale
                        // 🆕 Injecter les données API dans les items locaux existants
                        const updatedItems = config.gridLayout.items.map(item => {
                            const apiInstance = apiInstancesMap.get(item.id);
                            if (apiInstance) {
                                const apiProps = apiInstance.props ?? apiInstance.data ?? {};
                                console.log(`🔄 Injection données API pour ${item.id}:`, apiProps);
                                // Fusionner les données API dans l'item local
                                return {
                                    ...item,
                                    props: {
                                        ...item.props,
                                        ...apiProps
                                    }
                                };
                            }
                            return item;
                        });
                        
                        console.log('📄 Mise à jour de la grille globale avec données API');
                        onConfigChange({
                            gridLayout: {
                                ...config.gridLayout,
                                items: updatedItems
                            }
                        });
                    }
                } else {
                    console.warn('⚠️ Aucune instance trouvée dans l\'API, utilisation de la config locale');
                    console.log('💡 Astuce: Synchronisez vos instances dans l\'onglet API');
                }

                loadedPagesRef.current.add(normalizedPageUrl);
                setTick(t => t + 1);
                onPageLoadStateChange?.(normalizedPageUrl, 'loaded');
            } catch (error) {
                console.error('❌ Erreur lors du chargement depuis l\'API:', error);
                // En cas d'erreur, continuer avec la config locale
                onPageLoadStateChange?.(currentPageUrl || '/', 'error');
            } finally {
                const normalizedPageUrl = currentPageUrl || '/';
                inFlightPagesRef.current.delete(normalizedPageUrl);
            }
        };

        loadDataFromApi();
    }, [config.api.enableSync, isReady, currentPageUrl, tick]);

    // Ce composant ne rend rien, il agit juste comme un effet
    return null;
};

/**
 * Fonction pour reset la configuration à l'état d'origine
 */
export const resetToOriginalConfig = (onConfigChange: (newConfig: Partial<Config>) => void): boolean => {
    try {
        const originalConfigStr = localStorage.getItem('gandc_config_original');
        if (originalConfigStr) {
            const originalConfig = JSON.parse(originalConfigStr);
            
            // Restaurer la grille d'origine
            onConfigChange({
                gridLayout: originalConfig.gridLayout,
                PagesList: originalConfig.PagesList
            });
            
            console.log('✅ Configuration resetée à l\'état d\'origine');
            return true;
        } else {
            console.warn('⚠️ Aucune configuration d\'origine trouvée');
            return false;
        }
    } catch (error) {
        console.error('❌ Erreur lors du reset:', error);
        return false;
    }
};

/**
 * Fonction pour resynchroniser tout depuis la config locale vers l'API
 */
export const resyncFromLocal = async (
    config: Config,
    syncInstances: (items: LayoutItem[], page?: string) => Promise<any>
): Promise<void> => {
    try {
        console.log('🔄 Resynchronisation depuis la config locale...');
        
        // 🆕 Récupérer la configuration actuelle depuis localStorage (inclut les modifications de l'éditeur)
        const savedConfig = isConfigLocalStorageEnabled(config) ? localStorage.getItem('gandc_config_backup') : null;
        let currentConfig = config;
        
        if (savedConfig) {
            try {
                currentConfig = JSON.parse(savedConfig);
                console.log('📁 Configuration actuelle trouvée dans localStorage - utilisation pour resync');
            } catch (error) {
                console.warn('⚠️ Erreur lors du parsing de la config sauvegardée, utilisation de la config par défaut');
            }
        } else {
            console.log('📄 Aucune configuration sauvegardée trouvée, utilisation de la config par défaut');
        }
        
        // Récupérer toutes les instances de toutes les pages
        const allLocalItems: LayoutItem[] = [];
        
        // Grille globale
        allLocalItems.push(...currentConfig.gridLayout.items.filter(i => !i.outsideGrid));
        
        // Grilles personnalisées des pages
        for (const page of currentConfig.PagesList) {
            if (page.gridLayout) {
                const pageItems = page.gridLayout.items
                    .filter(i => !i.outsideGrid)
                    .map(i => ({ ...i, page: page.pageUrl }));
                allLocalItems.push(...pageItems);
            }
        }
        
        console.log(`📦 Total items locaux à synchroniser: ${allLocalItems.length}`);
        console.log('📦 Items locaux:', allLocalItems);
        
        // Synchroniser toutes les instances
        const result = await syncInstances(allLocalItems);
        
        if (result) {
            console.log('✅ Resynchronisation terminée:', result);
        }
    } catch (error) {
        console.error('❌ Erreur lors de la resynchronisation:', error);
        throw error;
    }
};

